import type { LayoutProps } from "../types";

export default function MagazineLayout({ theme, template, articles, language }: LayoutProps) {
  const t = theme;
  const [cover, ...rest] = articles;
  const featured = rest.slice(0, 3);
  const lower = rest.slice(3);
  const issueLabel = language === "en" ? "Issue" : "Numero";
  const featuredLabel = language === "en" ? "In this issue" : "Ce numero";

  return (
    <div style={{ background: t.palette.bg, color: t.palette.ink, fontFamily: t.typography.body }}>
      {/* Cover */}
      <section className="relative">
        <div className="relative h-[60vh] min-h-[480px] w-full overflow-hidden">
          {cover && <img src={cover.imageUrl} alt="" className="h-full w-full object-cover" />}
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 30%, ${t.palette.bg} 100%)` }} />
          <div className="absolute inset-0 flex flex-col items-center justify-end px-8 pb-16 text-center">
            <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.32em" }} className="text-[11px] uppercase">
              {issueLabel} 47 &middot; {cover?.date}
            </span>
            <h1
              style={{
                fontFamily: t.typography.masthead,
                color: t.palette.ink,
                letterSpacing: "0.02em",
                textShadow: `0 1px 0 ${t.palette.bg}`,
              }}
              className="mt-3 max-w-4xl text-5xl font-bold leading-[1.05] md:text-7xl"
            >
              {template.name}
            </h1>
            <div style={{ background: t.palette.accent }} className="mt-6 h-px w-24" />
            {cover && (
              <p style={{ fontFamily: t.typography.body, color: t.palette.muted }} className="mt-6 max-w-2xl text-base italic md:text-lg">
                {cover.dek}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Cover article body */}
      {cover && (
        <article className="mx-auto max-w-3xl px-6 py-16">
          <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.18em" }} className="text-xs uppercase">
            {cover.kicker}
          </span>
          <h2 style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.02em" }} className="mt-4 text-4xl font-bold leading-tight md:text-5xl">
            {cover.title}
          </h2>
          <p style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.12em" }} className="mt-3 text-xs uppercase">
            Par {cover.byline}
          </p>
          <div className="mt-10 space-y-6">
            {cover.body.map((p, i) => (
              <p key={i} style={{ fontFamily: t.typography.body, color: t.palette.ink }} className="text-lg leading-9">
                {i === 0 && (
                  <span style={{ fontFamily: t.typography.headline, fontSize: "5rem", float: "left", lineHeight: "0.85", paddingRight: "0.75rem", color: t.palette.accent }}>
                    {p.charAt(0)}
                  </span>
                )}
                {i === 0 ? p.slice(1) : p}
              </p>
            ))}
          </div>
          <div style={{ background: t.palette.accent }} className="mx-auto mt-12 h-px w-16" />
        </article>
      )}

      {/* Featured pair */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <h3 style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.32em", textAlign: "center" }} className="mb-10 text-[11px] uppercase">
          &mdash; {featuredLabel} &mdash;
        </h3>
        <div className="grid gap-12 md:grid-cols-3">
          {featured.map((a) => (
            <article key={a.id} className="text-center">
              <img src={a.imageUrl} alt="" className="mx-auto h-56 w-full object-cover" style={{ filter: "grayscale(35%)" }} />
              <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.2em" }} className="mt-5 inline-block text-[10px] uppercase">
                {a.kicker}
              </span>
              <h4 style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.01em" }} className="mt-2 text-2xl font-semibold leading-snug">
                {a.title}
              </h4>
              <p style={{ fontFamily: t.typography.body, color: t.palette.muted }} className="mt-3 text-sm italic leading-7">
                {a.dek}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Lower spread - alternating */}
      <section className="mx-auto max-w-5xl px-6 pb-20">
        {lower.map((a, i) => (
          <article key={a.id} className={`grid items-center gap-8 py-10 md:grid-cols-2 ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`} style={{ borderTop: `1px solid ${t.palette.rule}` }}>
            <img src={a.imageUrl} alt="" className="h-64 w-full object-cover" style={{ direction: "ltr" }} />
            <div style={{ direction: "ltr" }}>
              <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.18em" }} className="text-[10px] uppercase">
                {a.kicker}
              </span>
              <h4 style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.015em" }} className="mt-2 text-3xl font-semibold leading-tight">
                {a.title}
              </h4>
              <p style={{ fontFamily: t.typography.body, color: t.palette.muted }} className="mt-3 text-base leading-7">
                {a.dek}
              </p>
              <p style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.12em" }} className="mt-4 text-[11px] uppercase">
                {a.byline} &middot; {a.date}
              </p>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
